import { useEffect, useState } from 'react';
import { Plus, Filter, Eye, MessageSquare, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input, Select } from '../components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { TicketDetailModal } from '../components/support/TicketDetailModal';
import { NewTicketForm } from '../components/support/NewTicketForm';
import { supportApi } from '../services/api';
import { formatDateTime, getStatusColor, getPriorityColor } from '../lib/utils';
import type { SupportTicket } from '../types';

const statusOptions = [
  { value: '', label: 'Allar stöður' },
  { value: 'open', label: 'Opið' },
  { value: 'in_progress', label: 'Í vinnslu' },
  { value: 'waiting_customer', label: 'Bíður viðskiptavinar' },
  { value: 'resolved', label: 'Leyst' },
  { value: 'closed', label: 'Lokað' },
];

const priorityOptions = [
  { value: '', label: 'Allir forgangar' },
  { value: 'low', label: 'Lágur' },
  { value: 'medium', label: 'Miðlungs' },
  { value: 'high', label: 'Hár' },
  { value: 'critical', label: 'Brýnt' },
];

const categoryOptions = [
  { value: '', label: 'Allir flokkar' },
  { value: 'billing', label: 'Reikningar' },
  { value: 'container', label: 'Gámur' },
  { value: 'technical', label: 'Tæknileg' },
  { value: 'general', label: 'Almennt' },
  { value: 'complaint', label: 'Kvörtun' },
];

const statusLabels: Record<string, string> = {
  open: 'Opið',
  in_progress: 'Í vinnslu',
  waiting_customer: 'Bíður viðskiptavinar',
  resolved: 'Leyst',
  closed: 'Lokað',
};

const priorityLabels: Record<string, string> = {
  low: 'Lágur',
  medium: 'Miðlungs',
  high: 'Hár',
  critical: 'Brýnt',
};

export function Tickets() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  async function loadTickets() {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.category = categoryFilter;

      const response = await supportApi.listTickets(params);
      setTickets(response.tickets);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Villa við að sækja beiðnir');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTickets();
  }, [statusFilter, priorityFilter, categoryFilter]);

  const filteredTickets = tickets.filter((ticket) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.id.toLowerCase().includes(query) ||
        ticket.subject.toLowerCase().includes(query) ||
        ticket.customer_name?.toLowerCase().includes(query) ||
        ticket.customer_email?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  function openDetailModal(ticketId: string) {
    setSelectedTicketId(ticketId);
    setDetailModalOpen(true);
  }

  function handleTicketUpdate(updatedTicket: SupportTicket) {
    setTickets(tickets.map((t) => (t.id === updatedTicket.id ? updatedTicket : t)));
  }

  function handleTicketCreated(newTicket: SupportTicket) {
    setTickets([newTicket, ...tickets]);
    setNewTicketModalOpen(false);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Þjónustubeiðnir</h1>
              <p className="mt-1 text-sm text-gray-500">
                Halda utan um beiðnir frá viðskiptavinum
              </p>
            </div>
            <Button onClick={loadTickets} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4" />
              Endurnýja
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Actions Bar */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Input
              type="search"
              placeholder="Leita að beiðni..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64"
            />
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4" />
              Síur
            </Button>
          </div>
          <Button onClick={() => setNewTicketModalOpen(true)}>
            <Plus className="h-4 w-4" />
            Ný beiðni
          </Button>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className="mb-6">
            <CardContent className="flex flex-wrap gap-4 pt-6">
              <Select
                label="Staða"
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-48"
              />
              <Select
                label="Forgangur"
                options={priorityOptions}
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-48"
              />
              <Select
                label="Flokkur"
                options={categoryOptions}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-48"
              />
            </CardContent>
          </Card>
        )}

        {/* Stats Summary */}
        <div className="mb-6 grid gap-4 sm:grid-cols-5">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-gray-900">
                {tickets.length}
              </div>
              <p className="text-sm text-gray-500">Heildarfjöldi</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-gray-600">
                {tickets.filter((t) => t.status === 'open').length}
              </div>
              <p className="text-sm text-gray-500">Opið</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">
                {tickets.filter((t) => t.status === 'in_progress').length}
              </div>
              <p className="text-sm text-gray-500">Í vinnslu</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600">
                {tickets.filter((t) => t.status === 'waiting_customer').length}
              </div>
              <p className="text-sm text-gray-500">Bíður svars</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">
                {tickets.filter((t) => t.status === 'resolved').length}
              </div>
              <p className="text-sm text-gray-500">Leyst</p>
            </CardContent>
          </Card>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 text-red-800">
            <p className="font-medium">Villa við að sækja beiðnir</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-litla-primary border-t-transparent mx-auto"></div>
                <p className="mt-2 text-gray-500">Hleð beiðnum...</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Tickets Table */
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Beiðni</TableHead>
                    <TableHead>Viðskiptavinur</TableHead>
                    <TableHead>Efni</TableHead>
                    <TableHead>Flokkur</TableHead>
                    <TableHead>Forgangur</TableHead>
                    <TableHead>Staða</TableHead>
                    <TableHead>Úthlutað</TableHead>
                    <TableHead>Búið til</TableHead>
                    <TableHead>Skilaboð</TableHead>
                    <TableHead className="text-right">Aðgerðir</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="py-12 text-center text-gray-500">
                        Engar beiðnir fundust
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTickets.map((ticket) => (
                      <TableRow key={ticket.id}>
                        <TableCell>
                          <button
                            onClick={() => openDetailModal(ticket.id)}
                            className="font-medium text-litla-primary hover:underline"
                          >
                            #{ticket.id.slice(0, 8)}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{ticket.customer_name}</div>
                            <div className="text-xs text-gray-500">{ticket.customer_email}</div>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{ticket.subject}</TableCell>
                        <TableCell>
                          <span className="capitalize">{ticket.category}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={getPriorityColor(ticket.priority)}>
                            {priorityLabels[ticket.priority]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(ticket.status)}>
                            {statusLabels[ticket.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {ticket.assigned_to_name || (
                            <span className="text-gray-400">Óúthlutað</span>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-500">
                          {formatDateTime(ticket.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-gray-500">
                            <MessageSquare className="h-4 w-4" />
                            <span>{ticket.message_count || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openDetailModal(ticket.id)}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                              title="Skoða"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicketId && (
        <TicketDetailModal
          isOpen={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedTicketId(null);
          }}
          ticketId={selectedTicketId}
          onUpdate={handleTicketUpdate}
        />
      )}

      {/* New Ticket Form */}
      <NewTicketForm
        isOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
        onCreate={handleTicketCreated}
      />
    </div>
  );
}
